/**
 * Twitter Text Adapter
 *
 * Integrates @ambassify/twitter-text library with Sweetistics-specific features.
 * Provides a bridge between Twitter's official text parsing and our custom logic.
 */

import twitterText from '@ambassify/twitter-text';
import type { LinkMetadata } from './fetch-metadata';
import { isTweetUrl } from './tweet-url-utils';

// Re-export commonly used functions from twitter-text
export const getTweetLength = twitterText.getTweetLength;
export const htmlEscape = twitterText.htmlEscape;

// Types for entities extracted by twitter-text
export interface TwitterEntity {
  cashtag: string | undefined;
  hashtag: string | undefined;
  indices: [number, number];
  listSlug: string | undefined;
  screenName: string | undefined;
  url: string | undefined;
}

export interface TwitterUrlEntity extends TwitterEntity {
  display_url?: string;
  expanded_url?: string;
  url: string;
}

export interface ParseOptions {
  cashtagClass?: string;
  cashtagUrlBase?: string;
  extractUrlsWithoutProtocol?: boolean;
  hashtagClass?: string;
  hashtagUrlBase?: string;
  htmlEscapeNonEntities?: boolean;
  invisibleTagAttrs?: string;
  linkAttributeBlock?: (entity: TwitterEntity, attributes: Record<string, string>) => Record<string, string>;
  linkTextBlock?: (entity: TwitterEntity, text: string) => string;
  metadata?: Record<string, LinkMetadata>;
  symbolTag?: string;
  targetBlank?: boolean;
  textWithSymbolTag?: string;
  urlClass?: string;
  urlEntities?: Record<string, TwitterUrlEntity>;
  usernameClass?: string;
  // Include the leading '@' symbol inside the username link
  // Matches twitter-text's "usernameIncludeSymbol" option
  usernameIncludeSymbol?: boolean;
  usernameUrlBase?: string;
}

export interface ParsedContent {
  cardUrls: TwitterUrlEntity[];
  entities: TwitterEntity[];
  html: string;
  inlineTweetUrls: Array<{ url: string; username: string; tweetId: string }>;
}

/**
 * Extract all entities from tweet text using twitter-text
 */
export function extractEntities(text: string, options?: { extractUrlsWithoutProtocol?: boolean }): TwitterEntity[] {
  const entities = twitterText.extractEntitiesWithIndices(text, options);
  return Array.isArray(entities) ? entities : [];
}

/**
 * Extract URLs with indices from tweet text
 */
export function extractUrls(text: string, options?: { extractUrlsWithoutProtocol?: boolean }): TwitterUrlEntity[] {
  const urls = twitterText.extractUrlsWithIndices(text, options);
  return Array.isArray(urls) ? urls : [];
}

/**
 * Parse tweet text and generate HTML with Sweetistics-specific features
 */
export function parseTweetWithEntities(text: string, options: ParseOptions = {}): ParsedContent {
  // Extract all entities using twitter-text
  const entities = extractEntities(text, {
    extractUrlsWithoutProtocol: options.extractUrlsWithoutProtocol ?? false,
  });

  // Separate different entity types for custom processing
  const urlEntities: TwitterUrlEntity[] = [];
  const cardUrls: TwitterUrlEntity[] = [];
  const inlineTweetUrls: Array<{ url: string; username: string; tweetId: string }> = [];

  // Process URL entities to identify cards and inline tweets
  const providedUrlEntities = options.urlEntities || {};

  for (const entity of entities) {
    if (entity.url) {
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      const urlEntity = entity as TwitterUrlEntity;
      const urlKey = urlEntity.url ?? '';
      const provided = urlKey ? providedUrlEntities[urlKey] : undefined;
      // Enrich with provided urlEntities from API if available (snake_case only)
      if (provided) {
        if (provided.expanded_url) {
          urlEntity.expanded_url = provided.expanded_url;
        }
        if (provided.display_url) {
          urlEntity.display_url = provided.display_url;
        }
      }
      const expandedUrl = urlEntity.expanded_url || urlEntity.url;

      // Check if this is a tweet URL for inline embedding
      if (isTweetUrl(expandedUrl)) {
        const match = expandedUrl.match(INLINE_TWEET_REGEX);
        if (match?.[1] && match[2]) {
          const tweetId = match[2].replaceAll('_', '');
          inlineTweetUrls.push({
            url: expandedUrl,
            username: match[1],
            tweetId,
          });
        }
      } else if (shouldShowAsCard(expandedUrl)) {
        cardUrls.push(urlEntity);
      } else {
        urlEntities.push(urlEntity);
      }
    }
  }

  // Configure autoLinkEntities options with Sweetistics styling
  const autoLinkOptions = {
    htmlEscapeNonEntities: options.htmlEscapeNonEntities ?? true,
    targetBlank: options.targetBlank ?? true,
    usernameUrlBase: options.usernameUrlBase ?? '/@',
    hashtagUrlBase: options.hashtagUrlBase ?? '/search?q=%23',
    cashtagUrlBase: options.cashtagUrlBase ?? '/search?q=%24',
    // Ensure '@' is part of the clickable link for mentions
    usernameIncludeSymbol: options.usernameIncludeSymbol ?? true,
    usernameClass: options.usernameClass ?? 'text-primary hover:underline user-link',
    hashtagClass: options.hashtagClass ?? 'text-primary hover:underline',
    urlClass: options.urlClass ?? 'text-primary hover:underline',
    cashtagClass: options.cashtagClass ?? 'text-primary hover:underline',
    invisibleTagAttrs: options.invisibleTagAttrs,
    linkAttributeBlock: (entity: TwitterEntity, attributes: Record<string, string>) => {
      // Add preview attributes for mentions
      if (entity.screenName) {
        attributes['data-username'] = entity.screenName;
        attributes['data-preview'] = 'user';
      }

      // For URL entities, add title attribute with expanded URL (Twitter best practice)
      if (entity.url && options.urlEntities) {
        const provided = entity.url ? options.urlEntities[entity.url] : undefined;
        const expandedUrl = provided?.expanded_url || entity.url;
        if (expandedUrl && expandedUrl !== entity.url) {
          // biome-ignore lint/complexity/useLiteralKeys: Record<string, string> requires bracket access
          attributes['title'] = expandedUrl;
        }
        if (isHttpUrl(expandedUrl)) {
          // biome-ignore lint/complexity/useLiteralKeys: Record<string, string> requires bracket access
          attributes['href'] = expandedUrl;
          attributes['data-tco-url'] = entity.url;
        }
      }

      // Apply custom attribute modifications if provided
      if (options.linkAttributeBlock) {
        const nextAttributes = options.linkAttributeBlock(entity, attributes);
        return nextAttributes;
      }

      return attributes;
    },
    // Use display_url for visible link text per Twitter best practices.
    // Fall back to expanded_url, then original text when unavailable.
    linkTextBlock: (entity: TwitterEntity, currentText: string) => {
      // If caller provided a custom handler, let it override ours explicitly.
      if (typeof options.linkTextBlock === 'function') {
        return options.linkTextBlock(entity, currentText);
      }
      if (entity.url && options.urlEntities) {
        const provided = entity.url ? options.urlEntities[entity.url] : undefined;
        const displayUrl = provided?.display_url;
        const expandedUrl = provided?.expanded_url;

        // Prefer display_url for link text (Twitter best practice)
        if (displayUrl) {
          return displayUrl;
        }
        // Fallback to expanded_url if display_url is not available
        if (expandedUrl) {
          return expandedUrl;
        }
      }
      return currentText;
    },
    symbolTag: options.symbolTag,
    textWithSymbolTag: options.textWithSymbolTag,
    urlEntities: options.urlEntities,
  };

  // Filter out entities that will be shown as cards or inline tweets
  const filteredEntities = entities.filter((entity) => {
    if (!entity.url) {
      return true;
    }
    // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
    const expandedUrl = (entity as TwitterUrlEntity).expanded_url || entity.url;
    return !(isTweetUrl(expandedUrl) || shouldShowAsCard(expandedUrl));
  });

  // Generate HTML using twitter-text autoLinkEntities
  let html = twitterText.autoLinkEntities(text, filteredEntities, autoLinkOptions);

  // Handle newlines - same as original implementation
  html = html.replaceAll(/\n\n+/g, '<br class="mb-2" />').replaceAll('\n', '<br />');

  return {
    html,
    entities,
    cardUrls: cardUrls.filter((c) => {
      const expanded = c.expanded_url || c.url;
      return !(expanded.includes('pic.twitter.com') || expanded.includes('pic.x.com'));
    }),
    inlineTweetUrls,
  };
}

const isHttpUrl = (value: string | undefined): boolean => {
  if (!value) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Determine if a URL should be shown as a card
 */
function shouldShowAsCard(url: string): boolean {
  try {
    // Skip tweet URLs - they will be shown as inline tweets
    if (isTweetUrl(url)) {
      return false;
    }

    // Skip direct media files
    if (MEDIA_EXTENSIONS_REGEX.test(url)) {
      return false;
    }

    // Skip Twitter image URLs
    if (url.includes('pic.x.com') || url.includes('pic.twitter.com')) {
      return false;
    }

    // Show cards for most other domains
    return true;
  } catch {
    // If URL parsing fails, don't show as card
    return false;
  }
}

/**
 * Check if a display URL is a Twitter image URL
 */
export function isTwitterImageUrl(url: string | undefined): boolean {
  if (!url) {
    return false;
  }
  return url.includes('pic.x.com') || url.includes('pic.twitter.com');
}

/**
 * Validate tweet text using twitter-text validation
 */
export function validateTweet(text: string): {
  valid: boolean;
  weightedLength: number;
  permillage: number;
  displayRangeStart: number;
  displayRangeEnd: number;
  validRangeStart: number;
  validRangeEnd: number;
} {
  return twitterText.parseTweet(text);
}
const INLINE_TWEET_REGEX = /(?:twitter\.com|x\.com)\/([^/]+)\/status(?:es)?\/([\d_]+)/i;
const MEDIA_EXTENSIONS_REGEX = /\.(?:jpg|jpeg|png|gif|webp|mp4|mov|avi)$/i;
